/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-array-index-key */
import type { ChatMessage } from '../../types/os';
import type { BrainChatMessage } from '../../types/brain-response';

import { AgentMessage, UserMessage } from './MessageItem';
import useStore from './store';
import useChatActions from './hooks/useChatActions';
import { Tooltip } from '@mantine/core';
import { memo } from 'react';
import {
  ToolCallProps,
  ReasoningStepProps,
  ReasoningProps,
  ReferenceData,
  Reference
} from '../../types/os';
import React, { type FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Brain,
  BookOpen,
  Wrench,
  Sparkles,
  Search,
  BarChart3,
  Mail,
  CheckSquare,
  CalendarClock,
  Rocket,
  ClipboardList
} from 'lucide-react';

import ChatBlankState from './ChatBlankSlate';
import BrainResponse from './brain/BrainResponse';
import { ConnectorDraftCard } from './messages/ConnectorDraftCard';
import { connectorsApi } from '../../services/connectorsApi';
import useWorkspaceStore from '../../store/workspace/workspaceStore';
import useAIChatStreamHandler from './hooks/useAIChatStreamHandler';

import {
  MetricsGraphResult
} from './ToolResults';
import ArtifactCard from './ArtifactCard';

interface MessageListProps {
  messages: ChatMessage[]
}

interface MessageWrapperProps {
  message: ChatMessage
}

interface ReferenceProps {
  references: ReferenceData[]
}

interface ReferenceItemProps {
  reference: Reference
}

const Reasoning: FC<ReasoningStepProps> = ({ index, stepTitle }) => (
  <div className="flex items-center gap-2 text-secondary">
    <div className="flex h-[20px] items-center rounded-md bg-background-secondary p-2">
      <p className="text-xs">STEP {index + 1}</p>
    </div>
    <p className="text-xs">{stepTitle}</p>
  </div>
);
const Reasonings: FC<ReasoningProps> = ({ reasoning }) => (
  <div className="flex flex-col items-start justify-center gap-2">
    {reasoning.map((title: any, index: any) => (
      <Reasoning
        key={`${title.title}-${title.action}-${index}`}
        stepTitle={title.title}
        index={index}
      />
    ))}
  </div>
);

/**
 * Get professional message for team member delegation
 */
const getTeamMemberMessage = (memberId: string) => {
  const memberMessages: Record<string, string[]> = {
    'entity-resolver': [
      '🔍 Searching for entity matches...',
      '📋 Resolving entity identifiers...',
      '🎯 Locating entity in database...',
      '🔎 Running entity lookup...'
    ],
    drilldown: [
      '📊 Analyzing detailed metrics...',
      '📈 Generating insights report...',
      '🔬 Processing data analysis...',
      '📋 Compiling comprehensive overview...'
    ],
    'email-composer': [
      '✉️ Drafting professional email...',
      '📝 Composing message content...',
      '📋 Structuring communication...',
      '💼 Preparing correspondence...'
    ],
    'task-creator': [
      '✅ Creating task items...',
      '📋 Organizing action items...',
      '🎯 Setting task priorities...',
      '📌 Scheduling follow-ups...'
    ],
    trendsearch: [
      '📊 Analyzing trend patterns...',
      '📈 Processing engagement metrics...',
      '🔍 Searching for top performers...',
      '📋 Compiling trend report...'
    ],
    objectsearch: [
      '📊 Analyzing object patterns...',
      '🔗 Resolving related objects...',
      '🔍 Searching dynamic object data...',
      '📋 Compiling object report...'
    ],
    object_search: [
      '📊 Analyzing object patterns...',
      '🔗 Resolving related objects...',
      '🔍 Searching dynamic object data...',
      '📋 Compiling object report...'
    ]
  };

  const messages = memberMessages[memberId] || [
    '🔄 Processing request...',
    '⚡ Specialist working on task...',
    '🎯 Agent handling request...'
  ];

  return messages[Math.floor(Math.random() * messages.length)];
};

// Compact JSON preview with truncation and word-wrapping
const JsonPreview = ({
  data,
  maxLines = 12,
  maxLineLength = 120,
  maxCharsTotal = 4000,
}: {
  data: any;
  maxLines?: number;
  maxLineLength?: number;
  maxCharsTotal?: number;
}) => {
  const [expanded, setExpanded] = useState(false);

  const full = useMemo(() => {
    try {
      return typeof data === 'string' ? data : JSON.stringify(data ?? {}, null, 2);
    } catch {
      return String(data ?? '');
    }
  }, [data]);

  const truncated = useMemo(() => {
    const text = full.length > maxCharsTotal ? `${full.slice(0, maxCharsTotal)}\n…` : full;
    const lines = text.split('\n');
    const trimmedLines = lines
      .slice(0, maxLines)
      .map((l) => (l.length > maxLineLength ? `${l.slice(0, maxLineLength)}…` : l));
    if (lines.length > maxLines) trimmedLines.push('…');
    return trimmedLines.join('\n');
  }, [full, maxLines, maxLineLength, maxCharsTotal]);

  const toShow = expanded ? full : truncated;

  return (
    <div className="space-y-2">
      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words" style={{ wordBreak: 'break-word' }}>{toShow}</pre>
      {full !== truncated && (
        <button type="button" onClick={() => setExpanded((e) => !e)} className="text-[11px] text-blue-600 hover:underline">
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

// Lightweight shimmer bar
const ShimmerBar = ({ width = '100%', height = 10 }: { width?: string; height?: number }) => (
  <div
    className="overflow-hidden rounded bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 animate-pulse"
    style={{ width, height }}
  >
    <div className="h-full w-full" />
  </div>
);

// Loading state for tool cards
const ToolLoadingState = ({ title, hint, progress }: { title: string; hint?: string; progress?: number }) => (
  <div className="border border-slate-200 rounded-xl bg-white p-3 shadow-sm">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-800">{title}</div>
          <div className="text-[11px] text-slate-500">{hint || 'Working on this action'}</div>
        </div>
      </div>
      <span className="text-[11px] text-blue-700 font-semibold">
        {progress !== undefined ? `${progress}%` : '…'}
      </span>
    </div>
    <div className="mt-2 space-y-2">
      <ShimmerBar width="100%" height={8} />
      <ShimmerBar width="70%" height={8} />
    </div>
  </div>
);

// Compact tile summarizing generic structured analysis data
const AnalysisSummaryTile = memo(({ data }: { data: any }) => {
  const obj = useMemo(() => {
    if (!data) return {} as any;
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return { insights: data }; }
    }
    return data;
  }, [data]);

  const chips: { label: string }[] = [];
  if (obj.entity_type) chips.push({ label: String(obj.entity_type) });
  if (obj.time_window || obj.timeframe?.window || obj.timeframe) {
    const tw = obj.time_window || obj.timeframe?.window || obj.timeframe;
    chips.push({ label: typeof tw === 'string' ? tw : 'custom window' });
  }
  if (typeof obj.total_results === 'number') chips.push({ label: `${obj.total_results} results` });
  if (typeof obj.execution_time === 'string' || typeof obj.execution_time === 'number') {
    const t = typeof obj.execution_time === 'number' ? `${obj.execution_time} ms` : obj.execution_time;
    chips.push({ label: t });
  }

  // Determine primary summary text
  const summary: string = useMemo(() => {
    const candidate = obj.analysis_summary || obj.insights || obj.summary || obj.message || '';
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    if (Array.isArray(obj.key_insights) && obj.key_insights.length > 0) return String(obj.key_insights[0]);
    if (Array.isArray(obj.recommendations) && obj.recommendations.length > 0) return `Prepared ${obj.recommendations.length} recommendation(s)`;
    return 'Analysis completed.';
  }, [obj]);

  const recommendations: string[] = useMemo(() => {
    const rec = obj.recommendations || obj.next_actions || obj.action_items || [];
    if (Array.isArray(rec)) return rec.slice(0, 3).map((r: any) => (typeof r === 'string' ? r : JSON.stringify(r)));
    return [];
  }, [obj]);

  return (
    <div className="cursor-default rounded-lg border p-3 text-xs bg-green-50 border-green-200">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm">✅</span>
        <span className="font-medium text-gray-700">Analysis Completed</span>
      </div>
      <div className="text-[12px] text-gray-700 mb-2 leading-relaxed line-clamp-3" style={{ display: '-webkit-box', WebkitLineClamp: 3 as any, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}>
        {summary}
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {chips.map((c, i) => (
            <span key={`${c.label}-${i}`} className="px-1.5 py-0.5 rounded bg-white border border-green-200 text-[10px] text-gray-600">
              {c.label}
            </span>
          ))}
        </div>
      )}
      {recommendations.length > 0 && (
        <div className="pt-1 border-t border-green-200">
          <div className="text-[11px] font-medium text-gray-700 mb-1">Top Recommendations</div>
          <ul className="space-y-1">
            {recommendations.map((r, idx) => (
              <li key={idx} className="text-[11px] text-gray-700 flex items-start gap-2">
                <span className="mt-1 w-1 h-1 bg-green-500 rounded-full" />
                <span className="leading-snug">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
});
AnalysisSummaryTile.displayName = 'AnalysisSummaryTile';

/**
 * Get icon for tool name  
 */
const getToolIcon = (toolName: string) => {
  const size = 16;
  const icons: Record<string, React.ReactNode> = {
    delegate_task_to_member: <ClipboardList size={size} />,
    global_search: <Search size={size} />,
    object_search: <Rocket size={size} />,
    fetch_metrics_graph: <BarChart3 size={size} />,
    send_email: <Mail size={size} />,
    create_task: <CheckSquare size={size} />,
    schedule_meeting: <CalendarClock size={size} />,
    read_skill: <BookOpen size={size} />,
    web_search: <Search size={size} />,
    web_research: <Search size={size} />
  };
  return icons[toolName] || <Sparkles size={size} />;
};

/**
 * Get completion message for tools
 */
const getCompletionMessage = (toolName: string, success: boolean) => {
  if (!success) return '❌ Task failed';

  const messages: Record<string, string> = {
    delegate_task_to_member: '✅ Task delegated successfully',
    global_search: '🎯 Search completed',
    object_search: '📊 Data retrieved',
    read_skill: '📖 Skill loaded',
    fetch_metrics_graph: '📈 Metrics loaded',
    send_email: '📤 Email sent',
    create_task: '✅ Task created',
    schedule_meeting: '📅 Meeting scheduled',
    generate_report: '📄 Report generated',
    export_data: '📤 Data exported',
    web_search: '🔍 Web search completed',
    web_research: '🔬 Research completed'
  };
  return messages[toolName] || '✅ Completed';
};

/**
 * Determines if a tool call should be hidden by default (verbose/irrelevant details)
 * Skills tool calls and other internal operations are hidden to reduce clutter
 */
const shouldHideToolByDefault = (toolName: string): boolean => {
  const hiddenTools = [
    // Skills-based tools that are just internal operations
    'get_skill',
    'list_skills',
    'search_skills',
    'execute_skill',
    // Other verbose internal tools
    'update_memory',
    'fetch_context',
    'log_event'
  ];

  return hiddenTools.includes(toolName);
};

/**
 * Progress indicator component for tool execution
 */
const ToolProgressIndicator = memo(({ progress, statusMessage, estimatedTimeRemaining }: {
  progress?: number;
  statusMessage?: string;
  estimatedTimeRemaining?: number;
}) => {
  if (!progress && !statusMessage && !estimatedTimeRemaining) return null;

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
      {progress !== undefined && (
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-slate-600">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              {statusMessage || 'Processing...'}
            </span>
            <span className="font-mono">{progress}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-blue-500 h-full rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      )}

      {estimatedTimeRemaining && estimatedTimeRemaining > 0 && (
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <div className="w-3 h-3 border border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          <span>Estimated time: {formatTime(estimatedTimeRemaining)}</span>
        </div>
      )}

      {statusMessage && !progress && (
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
          <span>{statusMessage}</span>
        </div>
      )}
    </div>
  );
});
ToolProgressIndicator.displayName = 'ToolProgressIndicator';

// Bespoke tool renderers -------------------------------------------------

const ObjectSearchCard = memo(({ data }: { data: any }) => {
  const rows = useMemo(
    () => data?.data || data?.result_rows || data?.result_data || [],
    [data]
  );

  // Flatten rows: hoist fields.* and related.* into top-level keys
  const flattenedRows = useMemo(() => {
    return rows.map((row: any) => {
      const flat: Record<string, any> = {};
      for (const [key, value] of Object.entries(row)) {
        if (key === 'fields' && value && typeof value === 'object' && !Array.isArray(value)) {
          for (const [fk, fv] of Object.entries(value as Record<string, any>)) {
            flat[fk] = fv;
          }
        } else if (key === 'related' && value && typeof value === 'object' && !Array.isArray(value)) {
          for (const [rk, rv] of Object.entries(value as Record<string, any>)) {
            flat[`related:${rk}`] = rv;
          }
        } else {
          flat[key] = value;
        }
      }
      return flat;
    });
  }, [rows]);

  // Build columns from flattened rows, skip object_id if always null
  const columns: string[] = useMemo(() => {
    if (data?.result_columns?.length) return data.result_columns;
    if (!flattenedRows[0]) return [];
    const allKeys = new Set<string>();
    flattenedRows.forEach((row: any) => {
      Object.keys(row).forEach((k) => allKeys.add(k));
    });
    // Put id/external_id/name first, related:* last
    const priority = ['id', 'external_id', 'name'];
    const sorted = Array.from(allKeys).sort((a, b) => {
      const aRel = a.startsWith('related:') ? 1 : 0;
      const bRel = b.startsWith('related:') ? 1 : 0;
      if (aRel !== bRel) return aRel - bRel;
      const aP = priority.indexOf(a);
      const bP = priority.indexOf(b);
      if (aP !== -1 && bP !== -1) return aP - bP;
      if (aP !== -1) return -1;
      if (bP !== -1) return 1;
      return a.localeCompare(b);
    });
    // Drop object_id if all null
    return sorted.filter((k) => {
      if (k === 'object_id') return flattenedRows.some((r: any) => r[k] != null);
      return true;
    });
  }, [data, flattenedRows]);

  const numericColumns = useMemo(
    () => columns.filter((c: string) => typeof flattenedRows?.[0]?.[c] === 'number'),
    [columns, flattenedRows]
  );

  const maxByColumn = useMemo(() => {
    const map: Record<string, number> = {};
    numericColumns.forEach((c: string) => {
      map[c] = Math.max(...flattenedRows.map((r: any) => Number(r?.[c] || 0)), 0);
    });
    return map;
  }, [numericColumns, flattenedRows]);

  const formatNumber = (value: any) => {
    if (typeof value !== 'number') return String(value ?? '—');
    if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value.toLocaleString();
  };

  const formatCellValue = (val: any): string => {
    if (val == null) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (Array.isArray(val)) return `${val.length} items`;
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const limitedRows = flattenedRows.slice(0, 10);
  const totalResults = data?.total_results ?? data?.total ?? rows.length;
  const executionTime = data?.execution_time
    ? `${data.execution_time}ms`
    : '—';

  return (
    <div className="border border-slate-200 rounded-xl bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Found {totalResults || '–'} {data?.entity_type || data?.object_type || 'results'} • {executionTime}
          </div>
          {data?.query && (
            <div className="text-xs text-slate-500">Query: "{data.query}"</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="px-3 py-1.5 text-xs rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50">
            Export CSV
          </button>
          <select className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-700 bg-white">
            <option>10 rows</option>
            <option>25 rows</option>
            <option>50 rows</option>
          </select>
          <button type="button" className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700">
            View Details
          </button>
        </div>
      </div>

      {columns.length === 0 ? (
        <div className="p-4 text-sm text-slate-600">No data returned.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                {columns.map((col: string) => (
                  <th key={col} className="text-left px-3 py-2 font-medium text-slate-700 border-b border-slate-100 whitespace-nowrap">
                    {col.startsWith('related:') ? col.replace('related:', '🔗 ') : col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {limitedRows.map((row: any, rowIdx: number) => (
                <tr key={rowIdx} className="border-b border-slate-100 hover:bg-slate-50/60">
                  {columns.map((col: string) => {
                    const val = row?.[col];
                    const isNumeric = typeof val === 'number' && numericColumns.includes(col);
                    const max = maxByColumn[col] || 1;
                    const pct = isNumeric ? Math.min(100, Math.round((Number(val) / max) * 100)) : 0;
                    return (
                      <td key={col} className="px-3 py-2 align-middle text-slate-800 max-w-[200px] truncate" title={formatCellValue(val)}>
                        {isNumeric ? (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{formatNumber(val)}</span>
                            <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-700">{formatCellValue(val)}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > limitedRows.length && (
            <div className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-100">
              Showing first {limitedRows.length} of {rows.length} rows
            </div>
          )}
        </div>
      )}
    </div>
  );
});
ObjectSearchCard.displayName = 'ObjectSearchCard';

const GlobalSearchGrid = memo(({ data }: { data: any }) => {
  const setInputMessage = useStore((s) => s.setInputMessage);
  const { focusChatInput } = useChatActions();

  const results = data?.results || [];
  const query = data?.query || '';

  const handleAnalyze = (name: string, externalId: string) => {
    const prompt = `Analyze account ${name} (${externalId})`;
    setInputMessage(prompt);
    focusChatInput();
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-900">
          Found {data?.total_found ?? results.length} matches{query ? ` for "${query}"` : ''}
        </div>
        <div className="text-[11px] text-slate-500">{data?.entity_type || 'entities'}</div>
      </div>
      {results.length === 0 ? (
        <div className="text-sm text-slate-600">No matches returned.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {results.map((item: any) => (
            <div key={item.external_id} className="border border-slate-200 rounded-lg p-3 flex flex-col gap-2 hover:border-blue-200 transition">
              <div className="flex items-center gap-3">
                {item.metadata?.avatar_url ? (
                  <img src={item.metadata.avatar_url} alt={item.name} className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs">
                    {item.name?.[0] || '?'}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{item.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {item.type} • {item.metadata?.domain || item.external_id}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>Confidence</span>
                <span className="font-medium text-slate-900">{Math.round((item.confidence || 0) * 100)}%</span>
              </div>
              <button
                type="button"
                onClick={() => handleAnalyze(item.name, item.external_id)}
                className="text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md px-3 py-1.5 border border-blue-100"
              >
                Analyze this account
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
GlobalSearchGrid.displayName = 'GlobalSearchGrid';

// Removed: EntityProfileCard, ConceptSearchView, NarrativeTimeline, WorkspaceSchemaExplorer
// All data queries now go through object_search → ObjectSearchCard

/**
 * Enhanced Tool Component with better status handling and UI inspired by assistant-ui patterns
 */
const ToolComponent = memo(({ tools, isExpanded: externalExpanded, onToggle }: ToolCallProps & { isExpanded?: boolean; onToggle?: (id: string | number) => void }) => {
  const { result } = tools as any;
  const hasError = tools.tool_call_error === true || tools.status === 'error';
  const isDelegateTask = tools.tool_name === 'delegate_task_to_member';
  const [localExpanded, setLocalExpanded] = useState(false);
  const [showAllArgs, setShowAllArgs] = useState(false);

  // Get witty message for delegation
  const memberId = isDelegateTask ? tools.tool_args?.member_id : null;
  const taskDescription = isDelegateTask ? tools.tool_args?.task_description : null;
  const delegationMessage = memberId ? getTeamMemberMessage(memberId) : null;

  // Determine tool status with enhanced logic
  const getToolStatus = () => {
    if (tools.status) return tools.status;
    if (hasError) return 'error';
    if (!result) return 'running';
    return 'complete';
  };

  const status = getToolStatus();
  const isLoading = status === 'running' && !result;
  const toolTitle = tools.tool_name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Tool';
  const isExpanded = typeof externalExpanded === 'boolean' ? externalExpanded : localExpanded;

  const toggleExpanded = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (onToggle) {
      onToggle(tools.tool_call_id || tools.tool_name);
    } else {
      setLocalExpanded((prev) => !prev);
    }
  };

  // Guard so auto-expand fires exactly once per tool_call. Without this the
  // effect re-runs on every stream chunk — `result` is a new object ref each
  // time — and `onToggle` flips expand state, producing a rapid flicker
  // ("Save Artifact" card visibly oscillating open/closed).
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (autoExpandedRef.current) return;
    if (hasError || (tools.tool_name === 'save_artifact' && result)) {
      autoExpandedRef.current = true;
      if (onToggle) {
        onToggle(tools.tool_call_id || tools.tool_name);
      } else {
        setLocalExpanded(true);
      }
    }
  }, [hasError, result, onToggle, tools.tool_call_id, tools.tool_name]);

  // Check if this tool has a specialized result component
  const hasSpecializedResult = useMemo(() => {
    if (!result) return false;
    return (
      tools.tool_name === 'object_search' ||
      tools.tool_name === 'global_search' ||
      tools.tool_name === 'fetch_metrics_graph' ||
      tools.tool_name === 'save_artifact' ||
      (result.query && result.results && result.total_found !== undefined) ||
      (result.metrics && result.timeframe)
    );
  }, [result, tools.tool_name]);

  // Get status-specific styling - Professional SaaS Design
  const getStatusStyles = () => {
    switch (status) {
      case 'running':
        return {
          container: 'ring-1 ring-blue-100 bg-white',
          icon: 'bg-blue-500 text-white',
          badge: 'bg-blue-50 text-blue-700 border border-blue-200',
          badgeIcon: '⟳',
          badgeText: 'Processing',
          dot: 'bg-blue-500'
        };
      case 'error':
        return {
          container: 'ring-1 ring-red-100 bg-white',
          icon: 'bg-red-500 text-white',
          badge: 'bg-red-50 text-red-700 border border-red-200',
          badgeIcon: '⚠',
          badgeText: 'Failed',
          dot: 'bg-red-500'
        };
      case 'complete':
        return {
          container: hasSpecializedResult
            ? 'ring-1 ring-purple-100 bg-white'
            : 'ring-1 ring-green-100 bg-white',
          icon: hasSpecializedResult ? 'bg-purple-500 text-white' : 'bg-green-500 text-white',
          badge: hasSpecializedResult
            ? 'bg-purple-50 text-purple-700 border border-purple-200'
            : 'bg-green-50 text-green-700 border border-green-200',
          badgeIcon: hasSpecializedResult ? '●' : '✓',
          badgeText: hasSpecializedResult ? 'Data Ready' : 'Complete',
          dot: hasSpecializedResult ? 'bg-purple-500' : 'bg-green-500'
        };
      default:
        return {
          container: 'border-l-slate-400 bg-white shadow-sm border border-slate-200',
          icon: 'bg-slate-500 text-white',
          badge: 'bg-slate-50 text-slate-700 border border-slate-200',
          badgeIcon: '○',
          badgeText: 'Pending',
          dot: 'bg-slate-400'
        };
    }
  };

  const styles = getStatusStyles();

  // Minimal render for read_skill (Always compact/hidden-ish)
  if (tools.tool_name === 'read_skill') {
    return (
      <div className="relative overflow-hidden rounded-lg border border-slate-100 bg-white/95 p-2 shadow-sm transition-all duration-200 flex items-center gap-2">
        <div className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-slate-800 bg-slate-100">
          {getToolIcon(tools.tool_name)}
        </div>
        <div className="text-xs font-semibold text-slate-900 truncate">Read Skill</div>
      </div>
    );
  }

  // Determine which result component to use
  const renderResult = () => {
    if (!result) {
      return null;
    }

    // Object Search (primary data tool)
    if (tools.tool_name === 'object_search') {
      return <ObjectSearchCard data={result} />;
    }

    // Global Search
    if (
      tools.tool_name === 'global_search' ||
      (result.query && result.results && result.total_found !== undefined)
    ) {
      return <GlobalSearchGrid data={result} />;
    }

    // Save Artifact
    if (tools.tool_name === 'save_artifact') {
      return <ArtifactCard data={result} />;
    }

    // Metrics Graph
    if (
      tools.tool_name === 'fetch_metrics_graph' ||
      (result.metrics && result.timeframe)
    ) {
      return <MetricsGraphResult data={result} />;
    }

    // Fallback to JSON preview
    return <JsonPreview data={result} maxLines={10} />;
  };

  // --- Render Compact View ---
  if (!isExpanded) {
    return (
      <div
        onClick={() => toggleExpanded()}
        className={`${styles.container} relative overflow-hidden rounded-xl border border-slate-100 p-2 shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-200 cursor-pointer h-full`}
      >
        <div className="flex items-center gap-2.5">
          {/* Tool Icon */}
          <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs ${styles.icon}`}>
            {getToolIcon(tools.tool_name)}
          </div>

          <div className="min-w-0 flex-1">
            <h4 className="font-semibold text-xs text-slate-900 truncate leading-tight">
              {toolTitle}
            </h4>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`w-1.5 h-1.5 rounded-full ${styles.dot} ${status === 'running' ? 'animate-pulse' : ''}`} />
              <span className="text-[10px] text-slate-500 truncate font-medium">
                {status === 'running'
                  ? 'Running...'
                  : (tools.metrics as any)?.duration
                    ? `${((tools.metrics as any).duration * 1000).toFixed(0)}ms`
                    : styles.badgeText}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Render Expanded View ---
  // Keeps the rich, detailed view for when the user wants to see everything
  return (
    <div className={`${styles.container} col-span-full relative overflow-hidden rounded-2xl border border-slate-100 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-3 shadow-[0_10px_30px_rgba(15,23,42,0.05)] transition-all duration-300 ease-in-out`}>
      {/* Header */}
      <div className="flex items-start gap-2 pt-1 mb-2">
        {/* Tool Icon */}
        <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm ${styles.icon}`}>
          {getToolIcon(tools.tool_name)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="font-semibold text-[13px] text-slate-900 truncate">
                {toolTitle}
              </h4>
              <div className="text-[10px] text-slate-500 truncate">
                {status === 'running' ? (tools.status_message || 'Executing...') : styles.badgeText}
              </div>
            </div>

            <div className="flex flex-col items-end gap-1">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-semibold rounded-full ${styles.badge} ${status === 'running' ? 'animate-pulse' : ''}`}>
                <span className={status === 'running' ? 'animate-spin' : ''}>{styles.badgeIcon}</span>
                <span>{styles.badgeText}</span>
              </span>
              <button
                type="button"
                onClick={toggleExpanded}
                className="flex-shrink-0 px-2.5 py-0.5 text-[10px] rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors duration-200"
              >
                Hide details
              </button>
            </div>
          </div>

          {/* Tool Arguments Preview */}
          {tools.tool_args && Object.keys(tools.tool_args).length > 0 && (
            <div className="text-[10px] text-slate-600 my-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono transition-all duration-200">
              <div className="flex flex-wrap gap-y-1">
                {Object.entries(tools.tool_args).slice(0, showAllArgs ? undefined : 2).map(([key, value]) => (
                  <span key={key} className="mr-4 inline-flex items-center">
                    <span className="text-slate-500">{key}:</span>
                    <span className="text-slate-700 ml-1" title={typeof value === 'object' ? JSON.stringify(value) : String(value)}>
                      {(() => { const s = typeof value === 'object' ? JSON.stringify(value) : String(value); return showAllArgs ? s : (s.slice(0, 18) + (s.length > 18 ? '…' : '')); })()}
                    </span>
                  </span>
                ))}

                {Object.keys(tools.tool_args).length > 2 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllArgs(!showAllArgs);
                    }}
                    className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors cursor-pointer"
                  >
                    {showAllArgs
                      ? 'Show less'
                      : `+${Object.keys(tools.tool_args).length - 2} more`}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Delegation message */}
          {delegationMessage && (
            <div className="text-[11px] text-slate-600 mb-2 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <span className="text-slate-500">👥</span>
              <span className="font-medium">{delegationMessage}</span>
            </div>
          )}

          {/* Task description */}
          {taskDescription && taskDescription.length < 80 && (
            <div className="text-[11px] text-slate-600 mb-2 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <span className="text-slate-500">📋</span>
              <span className="italic">&ldquo;{taskDescription}&rdquo;</span>
            </div>
          )}

          {/* Execution Time */}
          {(tools.metrics as any)?.duration && (
            <div className="text-[11px] text-slate-500 flex items-center max-w-[100px] gap-2 bg-slate-50 border border-slate-200 rounded px-2 mt-2 py-1">
              <span className="text-slate-400">⚡</span>
              <span className="font-mono">{((tools.metrics as any).duration * 1000).toFixed(0)}ms</span>
            </div>
          )}

          {/* Error Message with Recovery Options */}
          {hasError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-red-500 mt-0.5">⚠</span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-red-800 mb-2">Execution Failed</div>
                  <div className="text-xs text-red-700 mb-3">
                    {typeof tools.tool_call_error === 'string'
                      ? tools.tool_call_error
                      : 'The tool encountered an error during execution.'}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors duration-200"
                      onClick={() => {
                        console.log('Retry tool execution:', tools.tool_call_id);
                      }}
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Error Details */}
              <div className="mt-4 pt-3 border-t border-red-200">
                <div className="text-xs text-red-600 bg-red-25 rounded p-3 font-mono">
                  <div className="font-medium mb-2 text-red-800">Error Details:</div>
                  <div className="whitespace-pre-wrap">
                    {JSON.stringify({
                      tool_name: tools.tool_name,
                      tool_call_id: tools.tool_call_id,
                      error: tools.tool_call_error,
                      args: tools.tool_args,
                      timestamp: new Date(tools.created_at * 1000).toISOString()
                    }, null, 2)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expandable Result Section */}
      <div className="mt-3 pt-3 border-t border-slate-200">
        <div className="bg-slate-50 rounded-lg p-3">
          {renderResult()}
        </div>
      </div>

      {/* Enhanced Progress Indicator */}
      <ToolProgressIndicator
        progress={tools.progress}
        statusMessage={tools.status_message || (status === 'running' ? 'Processing...' : undefined)}
        estimatedTimeRemaining={tools.estimated_time_remaining}
      />
    </div>
  );
});
ToolComponent.displayName = 'ToolComponent';

/**
 * Progress indicator component showing elapsed time and activity
 */
const ProgressIndicator = memo(({ elapsedMs, isThinking }: { elapsedMs?: number; isThinking?: boolean }) => {
  const formatElapsedTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  if (!elapsedMs && !isThinking) return null;

  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <div className="flex gap-1">
        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      {elapsedMs ? (
        <span>{formatElapsedTime(elapsedMs)}</span>
      ) : isThinking ? (
        <span>Processing...</span>
      ) : null}
    </div>
  );
});
ProgressIndicator.displayName = 'ProgressIndicator';

/**
 * Loading state component for AI thinking
 */
const AIThinkingState = memo(() => (
  <div className="flex items-start gap-4 py-4">
    <div className="rounded-lg bg-gradient-to-br from-blue-50 to-purple-50 p-1">
      <div className="w-4 h-4 bg-gradient-to-br from-blue-400 to-purple-400 rounded animate-pulse" />
    </div>
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="text-sm font-medium text-gray-700">HyperEngage Brain is thinking...</div>
        <ProgressIndicator isThinking />
      </div>
      <div className="text-xs text-gray-500">
        {[
          '🔍 Analyzing request...',
          '📊 Processing data...',
          '🎯 Preparing response...',
          '⚡ Computing insights...',
          '📋 Gathering information...'
        ][Math.floor(Math.random() * 5)]}
      </div>
    </div>
  </div>
));
AIThinkingState.displayName = 'AIThinkingState';

/**
 * Component for displaying agent thinking/reasoning content
 */
const ThinkingDisplay = memo(({ thinking }: { thinking: string }) => (
  <div className="flex items-start gap-4">
    <div className="rounded-lg bg-gradient-to-br from-purple-50 to-blue-50 p-1">
      <div className="w-4 h-4 bg-gradient-to-br from-purple-400 to-blue-400 rounded animate-pulse" />
    </div>
    <div className="flex flex-col gap-2 flex-1">
      <div className="text-xs font-medium text-gray-700">Agent is thinking...</div>
      <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-200 leading-relaxed">
        {thinking}
      </div>
    </div>
  </div>
));
ThinkingDisplay.displayName = 'ThinkingDisplay';

/**
 * Component for showing reasoning process
 */
const ReasoningProcess = memo(({ step, isLast }: { step: string; isLast: boolean }) => (
  <div className="flex items-start gap-3 py-2">
    <div className="flex flex-col items-center">
      <div className="w-2 h-2 bg-purple-400 rounded-full" />
      {!isLast && <div className="w-0.5 h-4 bg-purple-200 mt-1" />}
    </div>
    <div className="flex-1">
      <div className="text-xs text-purple-700 bg-purple-50 rounded px-2 py-1">
        🧠 {step}
      </div>
    </div>
  </div>
));
ReasoningProcess.displayName = 'ReasoningProcess';

/**
 * Component for memory updates
 */
const MemoryUpdate = memo(({ type }: { type: 'started' | 'completed' }) => (
  <div className="flex items-center gap-3 py-2">
    <div className="rounded-lg bg-green-50 p-1">
      <div className="w-3 h-3 bg-green-400 rounded animate-pulse" />
    </div>
    <div className="text-xs text-green-700">
      {type === 'started' ? '💾 Updating team memory...' : '✅ Memory updated successfully'}
    </div>
  </div>
));
MemoryUpdate.displayName = 'MemoryUpdate';

/**
 * Component for team run status and events
 */
const TeamEventStatus = memo(({ event, teamName, content }: {
  event: string;
  teamName?: string;
  content?: string;
}) => {
  const getEventInfo = (eventType: string) => {
    switch (eventType) {
      case 'TeamRunStarted':
        return { icon: '🚀', message: 'Analysis started', color: 'blue', bg: 'bg-blue-50' };
      case 'TeamRunCompleted':
        return { icon: '✅', message: 'Analysis completed', color: 'green', bg: 'bg-green-50' };
      case 'TeamRunError':
        return { icon: '❌', message: 'Analysis failed', color: 'red', bg: 'bg-red-50' };
      case 'TeamRunCancelled':
        return { icon: '⏹️', message: 'Analysis cancelled', color: 'yellow', bg: 'bg-yellow-50' };
      case 'TeamReasoningStarted':
        return { icon: '🧠', message: 'Reasoning process started', color: 'purple', bg: 'bg-purple-50' };
      case 'TeamReasoningCompleted':
        return { icon: '🎯', message: 'Reasoning completed', color: 'purple', bg: 'bg-purple-50' };
      case 'TeamMemoryUpdateStarted':
        return { icon: '💾', message: 'Updating memory...', color: 'indigo', bg: 'bg-indigo-50' };
      case 'TeamMemoryUpdateCompleted':
        return { icon: '✓', message: 'Memory updated', color: 'indigo', bg: 'bg-indigo-50' };
      case 'TeamParserModelResponseStarted':
        return { icon: '⚙️', message: 'Parsing response...', color: 'gray', bg: 'bg-gray-50' };
      case 'TeamParserModelResponseCompleted':
        return { icon: '✓', message: 'Response parsed', color: 'gray', bg: 'bg-gray-50' };
      case 'TeamOutputModelResponseStarted':
        return { icon: '📤', message: 'Generating output...', color: 'blue', bg: 'bg-blue-50' };
      case 'TeamOutputModelResponseCompleted':
        return { icon: '📋', message: 'Output generated', color: 'blue', bg: 'bg-blue-50' };
      default:
        return { icon: '🔄', message: 'Processing...', color: 'gray', bg: 'bg-gray-50' };
    }
  };

  const { icon, message, color, bg } = getEventInfo(event);

  return (
    <div className={`flex items-start gap-3 ${bg} rounded-lg p-2 mb-2`}>
      <div className="flex-shrink-0 mt-0.5">
        <span className="text-sm">{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-medium text-${color}-700`}>
          {message} {teamName && <span className="text-gray-500">• {teamName}</span>}
        </div>
        {content && (
          <div className="text-xs text-gray-600 mt-1 truncate">
            {content}
          </div>
        )}
      </div>
    </div>
  );
});
TeamEventStatus.displayName = 'TeamEventStatus';

/**
 * Entity Disambiguation Component
 */
const EntityDisambiguation = memo(({ data }: { data: any }) => {
  const setInputMessage = useStore((s) => s.setInputMessage);
  const { focusChatInput } = useChatActions();

  const handleSelect = (match: any) => {
    const type = (match?.type || 'entity').toString();
    const name = (match?.name || '').toString();
    // Populate the chat input with a clear disambiguation instruction
    const prompt = `Use ${type}: ${name}`;
    setInputMessage(prompt);
    focusChatInput();
  };

  const isNotFound = data.status === 'not_found';
  const hasMatches = data.matches && data.matches.length > 0;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
          <span className="text-amber-600">{isNotFound ? '🔍' : '❓'}</span>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-amber-800 mb-1">
            {isNotFound ? 'Entity Not Found' : 'Multiple Matches Found'}
          </h3>
          <p className="text-sm text-amber-700">{data.suggested_action}</p>
          {data.query && (
            <p className="text-xs text-amber-600 mt-1">Search query: &quot;{data.query}&quot;</p>
          )}
        </div>
      </div>

      {hasMatches && (
        <div className="space-y-2">
          {data.matches.map((match: any, index: number) => (
            <div
              key={match.external_id || index}
              onClick={() => handleSelect(match)}
              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-amber-200 hover:border-amber-300 cursor-pointer transition-colors"
            >
              <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs font-medium text-amber-700">
                {index + 1}
              </div>
              {match.metadata?.avatar_url && (
                <img src={match.metadata.avatar_url} alt="" className="w-6 h-6 rounded-full" />
              )}
              <div className="flex-1">
                <div className="font-medium text-gray-900">{match.name}</div>
                <div className="text-xs text-gray-500 capitalize">{match.type} • {match.metadata?.domain || match.external_id}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isNotFound && !hasMatches && (
        <div className="bg-white rounded-lg border border-amber-200 p-3 text-center">
          <p className="text-sm text-gray-600 mb-2">No entities found matching &quot;{data.query}&quot;</p>
          <p className="text-xs text-gray-500">Try searching with a more specific name or check the spelling.</p>
        </div>
      )}
    </div>
  );
});
EntityDisambiguation.displayName = 'EntityDisambiguation';

/**
 * Entity Analysis Component
 */
const EntityAnalysis = memo(({ data }: { data: any }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
        <span className="text-blue-600">📊</span>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-blue-800 mb-1">
          Entity Analysis: {data.entity?.name || 'Unknown Entity'}
        </h3>
        {data.analysis?.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            {Object.entries(data.analysis.summary).map(([key, value]: [string, any]) => (
              <div key={key} className="bg-white rounded p-2 text-center">
                <div className="text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</div>
                <div className="text-sm font-medium text-gray-900">{value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {data.analysis?.insights && (
      <div className="space-y-2 mb-4">
        <h4 className="text-xs font-medium text-gray-700">Key Insights</h4>
        {data.analysis.insights.map((insight: any, index: number) => (
          <div
            key={index}
            className={`p-2 rounded text-xs ${insight.type === 'opportunity' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
          >
            <span className="font-medium">{insight.title}:</span> {insight.description}
          </div>
        ))}
      </div>
    )}

    {data.next_actions && (
      <div className="border-t border-blue-200 pt-3">
        <h4 className="text-xs font-medium text-gray-700 mb-2">Recommended Actions</h4>
        <ul className="space-y-1">
          {data.next_actions.map((action: string, index: number) => (
            <li key={index} className="text-xs text-gray-600 flex items-center gap-2">
              <span className="w-1 h-1 bg-blue-400 rounded-full" />
              {action}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
));
EntityAnalysis.displayName = 'EntityAnalysis';

/**
 * Qualitative Update Component
 */
const QualitativeUpdate = memo(({ data }: { data: any }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
    <div className="flex items-start gap-3 mb-3">
      <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center">
        <span className="text-slate-600 text-sm">📋</span>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">
          Qualitative Insights
          {data.entity_type && data.entity_external_ids?.length > 0 && (
            <span className="ml-2 text-xs text-slate-600">
              {data.entity_type} • {data.entity_external_ids.length} entities
            </span>
          )}
        </h3>
        {data.timeframe && (
          <p className="text-xs text-slate-600 mb-1">Timeframe: {data.timeframe}</p>
        )}
        {data.summary && (
          <p className="text-sm text-slate-700">{data.summary}</p>
        )}
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
      {/* Highlights Section */}
      {data.highlights && data.highlights.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
          <h4 className="text-xs font-semibold text-emerald-800 mb-2 flex items-center gap-1">
            <span className="text-emerald-600">✨</span> Highlights
          </h4>
          <div className="space-y-1.5">
            {data.highlights.map((highlight: string, index: number) => (
              <div key={index} className="text-xs text-emerald-700 leading-relaxed">
                • {highlight}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risks Section */}
      {data.risks && data.risks.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
          <h4 className="text-xs font-semibold text-rose-800 mb-2 flex items-center gap-1">
            <span className="text-rose-600">⚠️</span> Risks
          </h4>
          <div className="space-y-1.5">
            {data.risks.map((risk: string, index: number) => (
              <div key={index} className="text-xs text-rose-700 leading-relaxed">
                • {risk}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Items Section */}
      {data.action_items && data.action_items.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <h4 className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1">
            <span className="text-blue-600">✅</span> Actions
          </h4>
          <div className="space-y-1.5">
            {data.action_items.map((action: string, index: number) => (
              <div key={index} className="text-xs text-blue-700 leading-relaxed">
                • {action}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Themes Section */}
      {data.themes && data.themes.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
          <h4 className="text-xs font-semibold text-purple-800 mb-2 flex items-center gap-1">
            <span className="text-purple-600">🏷️</span> Themes
          </h4>
          <div className="space-y-2">
            {data.themes.map((theme: any, index: number) => (
              <div key={index}>
                <div className="text-xs font-medium text-purple-800 mb-1">{theme.topic || theme.title}</div>
                {theme.quotes && theme.quotes.length > 0 && (
                  <div className="space-y-1">
                    {theme.quotes.slice(0, 1).map((quote: any, qIndex: number) => (
                      <div key={qIndex} className="text-xs text-purple-600 italic pl-2 border-l-2 border-purple-300">
                        &quot;{typeof quote === 'string' ? quote : quote.text}&quot;
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    {/* Quotes and Evidence Section */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      {/* Quotes Section */}
      {data.quotes && data.quotes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 lg:col-span-2">
          <h4 className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
            <span className="text-amber-600">💬</span> Key Quotes
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.quotes.slice(0, 4).map((quote: any, index: number) => (
              <div key={index} className="bg-white/60 border border-amber-200 rounded p-2">
                <p className="text-xs text-amber-800 italic mb-1 line-clamp-3">
                  &quot;{typeof quote === 'string' ? quote : quote.text || quote.snippet}&quot;
                </p>
                {quote.created_at && (
                  <p className="text-xs text-amber-600">
                    {new Date(quote.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Evidence Section */}
      {data.evidence && data.evidence.length > 0 && (
        <div className="bg-teal-50 border border-teal-200 rounded-md p-3">
          <h4 className="text-xs font-semibold text-teal-800 mb-2 flex items-center gap-1">
            <span className="text-teal-600">📄</span> Evidence
          </h4>
          <div className="space-y-2">
            {data.evidence.slice(0, 3).map((evidence: any, index: number) => (
              <div key={index} className="bg-white/60 border border-teal-200 rounded p-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs font-medium text-teal-800 capitalize">{evidence.object_type}</span>
                  {evidence.created_at && (
                    <span className="text-xs text-teal-600">
                      {new Date(evidence.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className="text-xs text-teal-700 line-clamp-2">{evidence.snippet}</p>
                {evidence.url && (
                  <a
                    href={evidence.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-teal-600 hover:text-teal-800 mt-1 inline-block"
                  >
                    View →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
));
QualitativeUpdate.displayName = 'QualitativeUpdate';

/**
 * Workspace Analysis Component
 */
const WorkspaceAnalysis = memo(({ data }: { data: any }) => (
  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
        <span className="text-purple-600">🏢</span>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-purple-800 mb-1">Workspace Analysis</h3>
        <p className="text-sm text-purple-700">{data.query}</p>
      </div>
    </div>

    {data.summary && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {Object.entries(data.summary).map(([key, value]: [string, any]) => (
          <div key={key} className="bg-white rounded p-2 text-center">
            <div className="text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</div>
            <div className="text-sm font-medium text-gray-900">{value}</div>
          </div>
        ))}
      </div>
    )}

    {data.segments && (
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-gray-700">Risk Segments</h4>
        {data.segments.map((segment: any, index: number) => (
          <div key={index} className="bg-white rounded-lg p-3 border border-purple-200">
            <div className="flex justify-between items-center mb-2">
              <span className={`text-xs font-medium px-2 py-1 rounded ${segment.risk_level === 'High'
                ? 'bg-red-100 text-red-700'
                : segment.risk_level === 'Medium'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-green-100 text-green-700'
                }`}
              >
                {segment.risk_level} Risk
              </span>
              <span className="text-xs text-gray-600">{segment.count} accounts</span>
            </div>
            {segment.accounts?.[0] && (
              <div className="text-xs text-gray-600">
                Example: {segment.accounts[0].name} (Score: {segment.accounts[0].health_score})
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
));
WorkspaceAnalysis.displayName = 'WorkspaceAnalysis';

/**
 * Comparison Analysis Component
 */
const ComparisonAnalysis = memo(({ data }: { data: any }) => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
        <span className="text-green-600">⚖️</span>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-green-800 mb-1">Entity Comparison</h3>
        <p className="text-sm text-green-700">
          {data.entities?.map((e: any) => e.name).join(' vs ')}
        </p>
      </div>
    </div>

    {data.metrics_comparison && (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-green-200">
              <th className="text-left py-2 text-gray-700">Metric</th>
              {data.entities?.map((entity: any) => (
                <th key={entity.external_id} className="text-right py-2 text-gray-700">
                  {entity.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.metrics_comparison).map(([metric, values]: [string, any]) => (
              <tr key={metric} className="border-b border-green-100">
                <td className="py-2 capitalize text-gray-600">{metric.replace('_', ' ')}</td>
                {Object.values(values).map((value: any, idx: number) => (
                  <td key={idx} className="text-right py-2 font-medium text-gray-900">{value}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
));
ComparisonAnalysis.displayName = 'ComparisonAnalysis';

/**
 * Error Response Component
 */
const ErrorResponse = memo(({ data }: { data: any }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
        <span className="text-red-600">❌</span>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-red-800 mb-1">Error</h3>
        <p className="text-sm text-red-700 mb-3">{data.message}</p>

        {data.suggestions && (
          <div>
            <h4 className="text-xs font-medium text-red-700 mb-2">Suggestions:</h4>
            <ul className="space-y-1">
              {data.suggestions.map((suggestion: string, index: number) => (
                <li key={index} className="text-xs text-red-600 flex items-center gap-2">
                  <span className="w-1 h-1 bg-red-400 rounded-full" />
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
));
ErrorResponse.displayName = 'ErrorResponse';

/**
 * Object Search Response Component
 */
const ObjectSearchResponse = memo(({ data }: { data: any }) => {
  const {
    entity_type: entityType = 'accounts',
    timeframe_analyzed: timeframeAnalyzed = '',
    time_window: timeWindow = 'last_30_days',
    time_zone: timeZone = 'UTC',
    total_results: totalResults = 0,
    execution_time: executionTime = '0ms',
    filter_summary: filterSummary = '',
    sort_field: sortField = '',
    sort_order: sortOrder = 'desc',
    sort_type: sortType = 'property',
    selected_fields: selectedFields = [],
    metrics = [],
    // Handle actual API response structure
    result_columns: resultColumns = [],
    result_rows_json: resultRowsJson, // NEW: JSON string format from backend
    result_rows: resultRows = [], // Legacy format
    // Keep fallback for original structure
    result_data: resultData = [],
    insights = '',
    key_insights: keyInsights = [],
    recommendations = [],
    data_sources_used: dataSourcesUsed = []
  } = data;

  // Parse result_rows_json if it exists (new format for OpenAI structured outputs compatibility)
  const tableRows = useMemo(() => {
    // Try new format first (JSON string)
    if (resultRowsJson && typeof resultRowsJson === 'string') {
      try {
        const parsed = JSON.parse(resultRowsJson);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        console.error('Failed to parse result_rows_json:', e);
      }
    }

    // Fallback to legacy formats
    if (resultRows.length > 0) return resultRows;
    return resultData;
  }, [resultRowsJson, resultRows, resultData]);

  // Use actual API structure (result_columns) or fallback to original
  const tableColumns = resultColumns.length > 0 ? resultColumns : [...selectedFields, ...metrics];

  const getSortTypeLabel = (type: string) => {
    const labels = {
      property: 'Property',
      growthval: 'Growth Value',
      changeval: 'Change Value'
    };
    return labels[type as keyof typeof labels] || type;
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
          <span className="text-purple-600">📊</span>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-800 mb-1">
            Object Search • {entityType}
          </h3>
          {timeframeAnalyzed && (
            <p className="text-xs text-gray-600">
              {timeframeAnalyzed} (time_window={timeWindow}, {timeZone})
            </p>
          )}
        </div>
      </div>

      {/* Summary Chips */}
      <div className="flex flex-wrap gap-2">
        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full border border-gray-200">
          {totalResults} results
        </span>
        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full border border-gray-200">
          {executionTime}
        </span>
        {filterSummary && (
          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full border border-gray-200">
            {filterSummary}
          </span>
        )}
        {sortField && (
          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium border border-purple-200">
            Sort: {getSortTypeLabel(sortType)} ({sortField} {sortOrder})
          </span>
        )}
      </div>

      {/* Table */}
      {tableRows && tableRows.length > 0 ? (
        <div className=" bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {tableColumns.map((column: any, index: number) => (
                    <th key={index} className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">
                      {column}
                      {column === sortField && (
                        <span className="ml-1 text-purple-500">
                          {sortOrder === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row: any, rowIndex: number) => (
                  <tr key={rowIndex} className="hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                    {tableColumns.map((column: any, colIndex: number) => (
                      <td key={colIndex} className="px-3 py-2 text-gray-700">
                        {Array.isArray(row) ? (row[colIndex] !== undefined ? String(row[colIndex]) : '-') : (row[column] !== undefined ? String(row[column]) : '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className=" bg-white rounded-lg border border-gray-200 p-4 text-center shadow-sm">
          <p className="text-sm text-gray-500">No results found</p>
          {(filterSummary || timeWindow) && (
            <p className="text-xs text-gray-400 mt-1">
              Try adjusting your filters or time window
            </p>
          )}
        </div>
      )}

      {/* Insights Panel */}
      {(insights || keyInsights.length > 0) && (
        <div className=" bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
          <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
            <span className="text-purple-600">📈</span> Insights
          </h4>
          {insights && (
            <p className="text-xs text-gray-700 mb-2">{insights}</p>
          )}
          {keyInsights.length > 0 && (
            <ul className="space-y-1">
              {keyInsights.map((insight: string, index: number) => (
                <li key={index} className="text-xs text-gray-600 flex items-start gap-2">
                  <span className="text-purple-400 mt-0.5">•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Recommendations Panel */}
      {recommendations.length > 0 && (
        <div className=" bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
          <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
            <span className="text-purple-600">💡</span> Recommendations
          </h4>
          <ul className="space-y-1">
            {recommendations.map((rec: string, index: number) => (
              <li key={index} className="text-xs text-gray-600 flex items-start gap-2">
                <span className="text-purple-400 mt-0.5">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Data Provenance Footer */}
      {dataSourcesUsed.length > 0 && (
        <div className=" pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            <span className="font-medium">Data sources:</span> {dataSourcesUsed.join(', ')}
          </p>
        </div>
      )}
    </div>
  );
});
ObjectSearchResponse.displayName = 'ObjectSearchResponse';

const ReferenceItem: FC<ReferenceItemProps> = ({ reference }) => (
  <div className="relative flex h-[63px] w-[190px] cursor-default flex-col justify-between overflow-hidden rounded-md bg-background-secondary p-3 transition-colors hover:bg-background-secondary/80">
    <p className="text-sm font-medium text-primary">{reference.name}</p>
    <p className="truncate text-xs text-primary/40">{reference.content}</p>
  </div>
);

const References: FC<ReferenceProps> = ({ references }) => (
  <div className="flex flex-col gap-4">
    {references.map((referenceData, index) => (
      <div
        key={`${referenceData.query}-${index}`}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-wrap gap-3">
          {referenceData.references.map((reference: any, refIndex: any) => (
            <ReferenceItem
              key={`${reference.name}-${reference.meta_data.chunk}-${refIndex}`}
              reference={reference}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/**
 * Detects response type from message content
 */
const detectResponseType = (content: string) => {
  // Try to parse JSON from content
  let parsed = null;

  // Check JSON code blocks first
  const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/g;
  let match;
  jsonBlockRegex.lastIndex = 0;

  // eslint-disable-next-line no-cond-assign
  while ((match = jsonBlockRegex.exec(content)) !== null) {
    try {
      parsed = JSON.parse(match[1].trim());
      break;
    } catch (e) {
      // Continue checking other JSON blocks
    }
  }

  // Try parsing entire content as JSON if no code blocks found
  if (!parsed) {
    try {
      parsed = JSON.parse(content);
    } catch (e) {
      // Not JSON, treat as plain text
      return { type: 'text', data: content };
    }
  }

  // Detect specific response types
  if (parsed.status === 'found_multiple' || (parsed.status === 'not_found' && parsed.suggested_action)) {
    return { type: 'entity_disambiguation', data: parsed };
  }

  if (parsed.error) {
    return { type: 'error', data: parsed };
  }

  if (parsed.comparison_type) {
    return { type: 'comparison', data: parsed };
  }

  // Entity analysis response (analysis or entity.external_id present)
  if (parsed.analysis_type || parsed.analysis || parsed.entity?.external_id) {
    return { type: 'entity_analysis', data: parsed };
  }

  // Object/trend search response compatibility
  if (parsed.report_type === 'trendsearch' || parsed.report_type === 'objectsearch' || parsed.report_type === 'object_search') {
    return { type: 'object_search', data: parsed };
  }

  // Qualitative Update response (has summary, highlights, risks, or action_items)
  if (parsed.summary && (parsed.highlights || parsed.risks || parsed.action_items || parsed.themes || parsed.quotes || parsed.evidence)) {
    return { type: 'qualitative_update', data: parsed };
  }

  if (parsed.query && (parsed.segments || parsed.trends || parsed.summary?.total_accounts)) {
    return { type: 'workspace_analysis', data: parsed };
  }

  // Check for traditional brain response format
  if (
    (parsed.version && parsed.intent && parsed.entity && parsed.summary) ||
    (parsed.entity_type && (parsed.metric_series || parsed.analysis_summary || parsed.metric_trends))
  ) {
    return { type: 'brain_response', data: parsed };
  }

  // Default to text if no specific type detected
  return { type: 'text', data: content };
};

/**
 * Converts API format to BrainResponseEnvelope format
 */
const convertToBrainFormat = (apiData: any) => {
  // If already in brain format, return as-is
  if (apiData.version && apiData.intent) {
    return apiData;
  }

  // Convert API format to brain format
  const charts = apiData.metric_series ? [{
    id: 'metrics-chart',
    title: `${apiData.entity_name || apiData.entity_type} Metrics`,
    type: 'line' as const,
    bucket_size: apiData.timeframe?.bucket_size || 'day',
    x_axis: {
      kind: 'date' as const,
      label: 'Date'
    },
    y_axis: {
      kind: 'number' as const,
      label: 'Count',
      unit: ''
    },
    series: apiData.metric_series.map((series: any) => ({
      name: series.name?.replace('metric_', '').replace(/_/g, ' ') || 'Metric',
      points: series.points.map((point: any) => ({
        t: point.t,
        v: point.v
      }))
    }))
  }] : [];

  const themes = apiData.metric_trends ? [{
    topic: 'Key Insights',
    mentions: apiData.metric_trends.length,
    avg_sentiment: 0.5,
    quotes: apiData.metric_trends.slice(0, 3).map((trend: any) => ({
      text: trend,
      author: 'System Analysis',
      ts: new Date().toISOString(),
      sentiment: 0.5
    }))
  }] : [];

  const actions = apiData.suggested_action ? [{
    type: 'create_task' as const,
    title: 'Follow Up Action',
    description: apiData.suggested_action,
    priority: 'normal' as const,
    due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
  }] : [];

  return {
    version: 'v1',
    intent: 'summarize',
    entity: {
      type: apiData.entity_type || 'account',
      id: apiData.entity_external_id,
      label: apiData.entity_name || apiData.entity_external_id || 'Unknown'
    },
    timeframe: apiData.timeframe ? {
      window: 'custom',
      start_iso: apiData.timeframe.start_iso,
      end_iso: apiData.timeframe.end_iso
    } : {
      window: 'last_30_days'
    },
    summary: apiData.analysis_summary || apiData.insights || 'Analysis complete',
    charts,
    themes,
    actions
  };
};

interface ConnectorDraftSectionProps extends MessageWrapperProps {
  draft: import('../../types/os').ConnectorDraft;
}

/**
 * Renders the ConnectorDraftCard for an agent message that carries a connector draft.
 * Isolated into its own component so hooks (useState) are always called unconditionally.
 * Accepts an explicit `draft` prop so multiple drafts per message can each have their own state.
 */
const ConnectorDraftSection = memo(({ message, draft }: ConnectorDraftSectionProps) => {
  // Seed local status from the draft itself so rehydrated cards start in the right state
  const [draftStatus, setDraftStatus] = useState<NonNullable<import('../../types/os').ConnectorDraft['status']>>(
    draft.status ?? 'pending'
  );
  const workspaceId =
    useWorkspaceStore((s) => s.workspace?.id) ||
    (typeof window !== 'undefined' ? localStorage.getItem('he_defws') : null);
  const sessionId = useStore((s) => s.sessionId);
  const { handleStreamResponse } = useAIChatStreamHandler();

  const sendUserMessage = (text: string) => {
    handleStreamResponse(text).catch((err: unknown) => {
      console.error('[ConnectorDraft] sendUserMessage failed', err);
    });
  };

  const handleApprove = async (fields: Record<string, unknown>) => {
    if (!workspaceId || !sessionId) return;
    setDraftStatus('sending');
    try {
      await connectorsApi.approveDraft(String(workspaceId), draft.draft_id, fields, sessionId);
      sendUserMessage(
        `[connector_action] approved draft draft_id=${draft.draft_id} provider=${draft.provider} action=${draft.action}`
      );
      setDraftStatus('sent');
    } catch (err: any) {
      console.error('[ConnectorDraft] approveDraft failed', err);
      if (err?.response?.status === 404 || err?.status === 404) {
        setDraftStatus('expired');
      } else {
        setDraftStatus('pending');
      }
    }
  };

  const handleReject = async () => {
    if (!workspaceId || !sessionId) return;
    setDraftStatus('sending');
    try {
      await connectorsApi.rejectDraft(String(workspaceId), draft.draft_id, undefined, sessionId);
      sendUserMessage(
        `[connector_action] rejected draft draft_id=${draft.draft_id}`
      );
      setDraftStatus('rejected');
    } catch (err: any) {
      console.error('[ConnectorDraft] rejectDraft failed', err);
      if (err?.response?.status === 404 || err?.status === 404) {
        setDraftStatus('expired');
      } else {
        setDraftStatus('pending');
      }
    }
  };

  const isDisabled =
    draftStatus === 'sending' || draftStatus === 'sent' || draftStatus === 'rejected';

  return (
    <div className="mt-2">
      <ConnectorDraftCard
        draft={draft}
        onApprove={handleApprove}
        onReject={handleReject}
        status={draftStatus}
        disabled={isDisabled}
      />
    </div>
  );
});
ConnectorDraftSection.displayName = 'ConnectorDraftSection';

// -----------------------------------------------------------------------------
// ConnectorDraftGroup — collapses multi-draft turns into an accordion.
// Stacking N fully-expanded cards is overwhelming (see screenshot feedback).
// Instead: show a compact header with count, render one expanded card at a
// time, and present the rest as one-line rows. Click a row to expand it (and
// auto-collapse the others). Cards that move to a terminal state (sent /
// rejected) auto-close and hand focus to the next still-pending draft.
// -----------------------------------------------------------------------------

interface CollapsedDraftRowProps {
  draft: import('../../types/os').ConnectorDraft;
  onExpand: () => void;
}

const PROVIDER_EMOJI: Record<string, string> = {
  gmail: '✉',
  google_calendar: '📅',
  notion: '📄',
  linear: '📋',
  hubspot: '🟠',
};

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground',
  sending: 'bg-muted text-muted-foreground animate-pulse',
  sent: 'bg-status-active/[0.12] text-status-active',
  rejected: 'bg-muted text-muted-foreground',
  expired: 'bg-muted text-muted-foreground',
};

const CollapsedDraftRow = ({ draft, onExpand }: CollapsedDraftRowProps) => {
  const status = draft.status ?? 'pending';
  const summary = draft.preview?.summary || `${draft.provider} · ${draft.action}`;
  const from = draft.preview?.from_account;
  return (
    <button
      type="button"
      onClick={onExpand}
      className="group flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5 text-left transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-muted text-[14px]"
      >
        {PROVIDER_EMOJI[draft.provider] ?? '•'}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{summary}</p>
        {from && (
          <p className="truncate text-[11px] text-muted-foreground">{from}</p>
        )}
      </div>
      <span
        className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${
          STATUS_STYLE[status] ?? STATUS_STYLE.pending
        }`}
      >
        {status}
      </span>
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
};

interface ConnectorDraftGroupProps extends MessageWrapperProps {
  drafts: import('../../types/os').ConnectorDraft[];
}

const ConnectorDraftGroup = memo(({ message, drafts }: ConnectorDraftGroupProps) => {
  // Pick the first still-pending draft to open by default; fall back to the
  // first draft if all are terminal (rehydrated completed set).
  const initiallyExpanded =
    drafts.find((d) => (d.status ?? 'pending') === 'pending')?.draft_id ??
    drafts[0]?.draft_id ??
    null;
  const [expandedId, setExpandedId] = useState<string | null>(initiallyExpanded);

  // If the currently-expanded draft moves to a terminal state, open the next
  // pending one automatically so the user keeps moving.
  useEffect(() => {
    const current = drafts.find((d) => d.draft_id === expandedId);
    const currentStatus = current?.status ?? 'pending';
    if (current && (currentStatus === 'sent' || currentStatus === 'rejected')) {
      const next = drafts.find(
        (d) => d.draft_id !== expandedId && (d.status ?? 'pending') === 'pending',
      );
      if (next) setExpandedId(next.draft_id);
    }
  }, [drafts, expandedId]);

  if (drafts.length === 0) return null;
  if (drafts.length === 1) {
    return <ConnectorDraftSection message={message} draft={drafts[0]} />;
  }

  const pendingCount = drafts.filter((d) => (d.status ?? 'pending') === 'pending').length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        <span>
          {pendingCount} of {drafts.length} pending approval
          {drafts.length === 1 ? '' : 's'}
        </span>
        {pendingCount === 0 && (
          <span className="text-status-active">All handled</span>
        )}
      </div>
      <div className="space-y-2">
        {drafts.map((draft) =>
          draft.draft_id === expandedId ? (
            <ConnectorDraftSection
              key={draft.draft_id}
              message={message}
              draft={draft}
            />
          ) : (
            <CollapsedDraftRow
              key={draft.draft_id}
              draft={draft}
              onExpand={() => setExpandedId(draft.draft_id)}
            />
          ),
        )}
      </div>
    </div>
  );
});
ConnectorDraftGroup.displayName = 'ConnectorDraftGroup';

const AgentMessageWrapper = ({ message }: MessageWrapperProps) => {
  let brainResponseData = (message as any).extra_data?.brain_response;
  let responseType = null;
  let responseData = null;

  // Check for brain response data in extra_data (legacy)
  if (!responseType && brainResponseData && typeof brainResponseData === 'object') {
    brainResponseData = convertToBrainFormat(brainResponseData);
  }

  // If not found in extra_data, try parsing from content and detect response type
  if (!responseType && !brainResponseData && message.content && typeof message.content === 'string') {
    const detected = detectResponseType(message.content);
    responseType = detected.type;
    responseData = detected.data;

    // Legacy brain response handling
    if (responseType === 'brain_response') {
      brainResponseData = convertToBrainFormat(responseData);
    }
  }

  // Filter tool calls to hide verbose/irrelevant ones by default
  const visibleToolCalls = message.tool_calls?.filter(toolCall => !shouldHideToolByDefault(toolCall.tool_name)) || [];
  const hiddenToolCalls = message.tool_calls?.filter(toolCall => shouldHideToolByDefault(toolCall.tool_name)) || [];
  const hasHiddenTools = hiddenToolCalls.length > 0;
  const [expandedToolId, setExpandedToolId] = useState<string | number | null>(null);

  return (
    <div className="flex flex-col gap-y-9">
      {/* Reasoning Steps */}
      {message.extra_data?.reasoning_steps &&
        message.extra_data.reasoning_steps.length > 0 && (
          <div className="flex items-start gap-4">
            <Tooltip
              label={<p className="text-accent">Reasoning</p>}
            >
              <div className="rounded-lg bg-background-secondary p-1">
                <Brain size={16} className="text-primary" />
              </div>
            </Tooltip>
            <div className="flex flex-col gap-3">
              <p className="text-xs uppercase">Reasoning</p>
              <Reasonings reasoning={message.extra_data.reasoning_steps} />
            </div>
          </div>
        )}
      {message.extra_data?.references &&
        message.extra_data.references.length > 0 && (
          <div className="flex items-start gap-4">
            <Tooltip
              label={<p className="text-accent">References</p>}
            >
              <div className="rounded-lg bg-background-secondary p-1">
                <BookOpen size={16} className="text-primary" />
              </div>
            </Tooltip>
            <div className="flex flex-col gap-3">
              <References references={message.extra_data.references} />
            </div>
          </div>
        )}
      {message.extra_data?.skills_used && message.extra_data.skills_used.length > 0 && (
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={14} className="text-purple-400 flex-shrink-0" />
          <div className="flex flex-wrap gap-1.5">
            {message.extra_data.skills_used.map((skill: any) => (
              <span
                key={skill.name}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-100"
              >
                {skill.display_name}
              </span>
            ))}
          </div>
        </div>
      )}
      {visibleToolCalls.length > 0 && (
        <div className="flex items-start gap-4">
          <Tooltip
            label={<p className="text-accent">Actions Performed</p>}
          >
            <div className="rounded-lg bg-background-secondary p-1">
              <Wrench size={16} className="text-primary" />
            </div>
          </Tooltip>

          <div className="flex-1">
            <div className="text-xs font-medium text-gray-700 mb-2">
              {visibleToolCalls.length} {visibleToolCalls.length === 1 ? 'action' : 'actions'} performed
              {hasHiddenTools && (
                <span className="text-gray-500 ml-1">
                  (+{hiddenToolCalls.length} internal)
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {visibleToolCalls.map((toolCall: any, index: any) => {
                const toolId = toolCall.tool_call_id || `${toolCall.tool_name}-${toolCall.created_at}-${index}`;
                return (
                  <ToolComponent
                    key={toolId}
                    tools={toolCall}
                    isExpanded={expandedToolId === toolId}
                    onToggle={(id) => setExpandedToolId((prev) => (prev === id ? null : id))}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Hidden tools indicator */}
      {hasHiddenTools && visibleToolCalls.length === 0 && (
        <div className="flex items-start gap-4">
          <Tooltip
            label={<p className="text-accent">Internal Actions</p>}
          >
            <div className="rounded-lg bg-background-secondary p-1">
              <Wrench size={16} className="text-primary" />
            </div>
          </Tooltip>
          <div className="flex-1">
            <div className="text-xs text-gray-500">
              {hiddenToolCalls.length} internal {hiddenToolCalls.length === 1 ? 'action' : 'actions'} performed
            </div>
          </div>
        </div>
      )}

      {/* Show thinking content from skills-based endpoint */}
      {message.thinking && (
        <ThinkingDisplay thinking={message.thinking} />
      )}

      {/* Connector draft approval card(s). Multiple drafts render as an
          accordion (one expanded at a time) rather than N stacked cards. */}
      {(() => {
        const drafts = message.connectorDrafts
          ?? (message.connectorDraft ? [message.connectorDraft] : []);
        if (!drafts.length) return null;
        return <ConnectorDraftGroup message={message} drafts={drafts} />;
      })()}

      <AgentMessage message={message} />
    </div>
  );
};

const Messages = ({ messages }: MessageListProps) => {
  if (messages.length === 0) {
    return <ChatBlankState />;
  }

  return (
    <div className="flex flex-col gap-4">
      {messages.map((message, index) => {
        // Hide internal connector-action markers from the visible chat.
        // These messages drive agent behaviour but are not meaningful to the user.
        if (
          message.role === 'user' &&
          typeof message.content === 'string' &&
          message.content.startsWith('[connector_action]')
        ) {
          return null;
        }

        const key = `${message.role}-${message.created_at}-${index}`;

        if (message.role === 'agent') {
          return (
            <AgentMessageWrapper
              key={key}
              message={message}
            />
          );
        }
        return <UserMessage key={key} message={message} />;
      })}
    </div>
  );
};

export default Messages;
